import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import API from "../api";
import { formatDate } from "../utils/formatDate";
import { formatMoney } from "../utils/formatMoney";

const DATE_FORMAT = "dd/MM/yyyy";
const PAGE_LIMIT = 50;

function getDefaultDateRange() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: first.toISOString().slice(0, 10),
    end: last.toISOString().slice(0, 10),
  };
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function readBlobError(err) {
  if (err?.response?.data instanceof Blob) {
    try {
      const text = await err.response.data.text();
      const parsed = JSON.parse(text);
      return parsed.message;
    } catch {
      /* fall through */
    }
  }
  return err?.response?.data?.message;
}

/**
 * Reusable page shell for single-metric, Shift-sourced advanced reports.
 * Server contract: the JSON endpoint returns
 *   { rows: [{ date, accountName, accountNo, tillCategory, [metricKey]: Number,
 *              tillUsers, shiftManager }],
 *     totals: { [totalsKey]: Number },
 *     pagination: { currentPage, totalPages, totalCount, limit },
 *     currency }
 * The CSV endpoint returns a text/csv Blob with UTF-8 BOM.
 */
export default function CashMetricReport({
  title,
  endpoint,
  exportEndpoint,
  metricKey,
  metricLabel,
  totalsKey,
  csvFilename,
}) {
  const EMPTY_TOTALS = { [totalsKey]: 0 };
  const INITIAL_STATE = {
    rows: [],
    totals: EMPTY_TOTALS,
    pagination: { currentPage: 1, totalPages: 0, totalCount: 0, limit: PAGE_LIMIT },
    currency: "GBP",
  };

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const [reportData, setReportData] = useState(INITIAL_STATE);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const requestIdRef = useRef(0);

  useEffect(() => {
    const { start, end } = getDefaultDateRange();
    setStartDate(start);
    setEndDate(end);
  }, []);

  useEffect(() => {
    let cancelled = false;
    API.get("/accounts", { params: { limit: 1000000 } })
      .then((res) => {
        if (!cancelled) setAccounts(res.data.accounts || []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || "Error fetching accounts");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const buildAccountParam = useCallback(() => {
    if (!selectedAccounts || selectedAccounts.length === 0) return "ALL";
    return selectedAccounts.map((a) => a._id).join(",");
  }, [selectedAccounts]);

  const validateRange = useCallback(() => {
    if (!startDate || !endDate) {
      setError("Start date and end date are required.");
      return false;
    }
    if (startDate > endDate) {
      setError("Start date cannot be after end date.");
      return false;
    }
    return true;
  }, [startDate, endDate]);

  const fetchReport = useCallback(
    async (pageNumber, append) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError("");
      setInfoMessage("");
      try {
        const params = {
          startDate,
          endDate,
          account: buildAccountParam(),
          page: pageNumber,
          limit: PAGE_LIMIT,
        };
        const res = await API.get(endpoint, { params });
        if (requestId !== requestIdRef.current) return;

        const incomingRows = Array.isArray(res.data.rows) ? res.data.rows : [];
        const incomingTotals = res.data.totals || EMPTY_TOTALS;
        const incomingPagination = res.data.pagination || INITIAL_STATE.pagination;
        const incomingCurrency = res.data.currency || "GBP";

        setReportData((prev) => ({
          rows: append ? [...prev.rows, ...incomingRows] : incomingRows,
          totals: incomingTotals,
          pagination: incomingPagination,
          currency: incomingCurrency,
        }));
        setPage(incomingPagination.currentPage || pageNumber);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setError(err.response?.data?.message || "Failed to load report");
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startDate, endDate, buildAccountParam, endpoint]
  );

  const handleGenerate = () => {
    if (!validateRange()) return;
    setReportData(INITIAL_STATE);
    setPage(1);
    fetchReport(1, false);
  };

  const handleLoadMore = () => {
    if (loading) return;
    if (page < reportData.pagination.totalPages) {
      fetchReport(page + 1, true);
    }
  };

  const handleExportCSV = async () => {
    if (!validateRange()) return;
    setExporting(true);
    setError("");
    setInfoMessage("");
    try {
      const res = await API.get(exportEndpoint, {
        params: {
          startDate,
          endDate,
          account: buildAccountParam(),
        },
        responseType: "blob",
      });
      triggerBlobDownload(
        new Blob([res.data], { type: "text/csv;charset=utf-8;" }),
        csvFilename
      );
      setInfoMessage("CSV downloaded.");
    } catch (err) {
      const serverMessage = await readBlobError(err);
      setError(serverMessage || "Error exporting CSV");
    } finally {
      setExporting(false);
    }
  };

  const { rows, totals, pagination, currency } = reportData;
  const hasRows = rows.length > 0;
  const showInitialSpinner = loading && rows.length === 0;
  const totalValue = Number(totals?.[totalsKey]) || 0;

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" sx={{ mb: 3, textAlign: "center" }}>
        {title}
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: endDate || undefined }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: startDate || undefined }}
          />
          <Autocomplete
            multiple
            options={accounts}
            getOptionLabel={(opt) => `${opt.accountName} (${opt.accountNo})`}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            value={selectedAccounts}
            onChange={(_, newValue) => setSelectedAccounts(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Accounts" placeholder="All Accounts" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option.accountName}
                  size="small"
                  {...getTagProps({ index })}
                  key={option._id}
                />
              ))
            }
            sx={{ minWidth: 300 }}
            ListboxProps={{ style: { maxHeight: 240 } }}
          />
        </Stack>

        <Box sx={{ mt: 1 }}>
          <Typography variant="caption">
            Date Range: {startDate} to {endDate}
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          sx={{ mt: 2 }}
        >
          <Button variant="contained" onClick={handleGenerate} disabled={loading}>
            {loading ? "Loading…" : "Generate Report"}
          </Button>
          <Button variant="outlined" onClick={handleExportCSV} disabled={exporting}>
            {exporting ? "Exporting…" : "Export to CSV"}
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {infoMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfoMessage("")}>
          {infoMessage}
        </Alert>
      )}

      {showInitialSpinner ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ overflowX: "auto", mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Till Category</TableCell>
                  <TableCell align="right">{metricLabel}</TableCell>
                  <TableCell>Till Users</TableCell>
                  <TableCell>Shift Manager</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hasRows ? (
                  rows.map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{formatDate(row.date, DATE_FORMAT)}</TableCell>
                      <TableCell>
                        {row.accountName}
                        {row.accountNo ? ` (${row.accountNo})` : ""}
                      </TableCell>
                      <TableCell>{row.tillCategory}</TableCell>
                      <TableCell align="right">
                        {formatMoney(Number(row[metricKey]) || 0, currency)}
                      </TableCell>
                      <TableCell>{row.tillUsers}</TableCell>
                      <TableCell>{row.shiftManager}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No {metricLabel.toLowerCase()} records in the selected range.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {hasRows && (
                <TableFooter>
                  <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                    <TableCell colSpan={3} sx={{ fontWeight: "bold" }}>
                      Totals (full filtered range):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {formatMoney(totalValue, currency)}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </Paper>

          {hasRows && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Showing {rows.length} of {pagination.totalCount} records
              </Typography>
              {page < pagination.totalPages && (
                <Button variant="contained" onClick={handleLoadMore} disabled={loading}>
                  {loading ? "Loading…" : "Load More"}
                </Button>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
